/**
 * @file Barre d'annonces rotative affichée au-dessus du header public.
 * @module packages.common.layouts.public.components.HeaderAnnouncementBar
 *
 * Filtre les annonces actives de l'établissement et les fait défiler
 * automatiquement toutes les 5 secondes quand il y en a plusieurs.
 * Ne rend rien si aucune annonce n'est active. Chaque annonce s'affiche dans
 * la langue du visiteur, ou à défaut dans la langue par défaut de
 * l'établissement (`message`).
 */
import React, {useState, useEffect} from 'react';
import PropTypes from 'prop-types';
import {Box, Container, Stack, Typography, alpha} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {getTranslatedField} from 'packages/common/utils/multiLanguageHelper';
import {getContrastTextColor} from 'packages/common/utils/colorUtils';

const ROTATION_INTERVAL_MS = 5000;

const HeaderAnnouncementBar = ({announcements, primaryColor}) => {
  const {i18n} = useTranslation();
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);

  const activeAnnouncements = announcements?.filter((announcement) => announcement.isActive) || [];

  // Rotation automatique uniquement s'il y a plusieurs annonces
  useEffect(() => {
    if (activeAnnouncements.length > 1) {
      const timer = setInterval(() => {
        setCurrentAnnouncement((prev) => (prev + 1) % activeAnnouncements.length);
      }, ROTATION_INTERVAL_MS);
      return () => clearInterval(timer);
    }
  }, [activeAnnouncements.length]);

  if (activeAnnouncements.length === 0) {
    return null;
  }

  return (
    <Box
      sx={{
        backgroundColor: alpha(primaryColor, 0.9),
        color: getContrastTextColor(primaryColor),
      }}
    >
      <Container maxWidth="xl">
        <Stack
          direction="row"
          justifyContent="center"
          alignItems="center"
          sx={{height: 40, position: 'relative'}}
        >
          <Typography
            variant="body1"
            sx={{
              fontWeight: 500,
              letterSpacing: '0.01em',
              textAlign: 'center',
            }}
          >
            {getTranslatedField(activeAnnouncements[currentAnnouncement], 'message', i18n.language)}
          </Typography>

          {activeAnnouncements.length > 1 && (
            <Stack
              direction="row"
              spacing={0.5}
              sx={{
                position: 'absolute',
                right: 2,
                top: '50%',
                transform: 'translateY(-50%)',
              }}
            >
              {activeAnnouncements.map((_, index) => (
                <Box
                  key={index}
                  sx={{
                    width: 4,
                    height: 4,
                    borderRadius: '50%',
                    backgroundColor: currentAnnouncement === index ?
                      getContrastTextColor(primaryColor) :
                      alpha(getContrastTextColor(primaryColor), 0.5),
                  }}
                />
              ))}
            </Stack>
          )}
        </Stack>
      </Container>
    </Box>
  );
};

HeaderAnnouncementBar.propTypes = {
  announcements: PropTypes.arrayOf(PropTypes.shape({
    isActive: PropTypes.bool,
    message: PropTypes.string,
    translations: PropTypes.shape({message: PropTypes.object}),
  })),
  primaryColor: PropTypes.string.isRequired,
};

export default HeaderAnnouncementBar;
